import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  resolveTenantByHost,
  type ResolvedHostTenant,
} from "@/services/hostTenantService";

// Session-scoped cache: the hostname a tab is loaded under does not change
// without a full reload, so a reload-friendly sessionStorage cache (rather
// than the localStorage+TTL pattern used by tenantBranding/brandKit) is
// enough to avoid re-resolving on every navigation within the tab.
const CACHE_TTL_MS = 15 * 60 * 1000;
const STORAGE_PREFIX = "host-tenant-cache:";

type HostTenantCacheEntry = {
  tenant: ResolvedHostTenant | null;
  expiresAt: string;
};

export type HostTenantStatus = "idle" | "loading" | "resolved" | "not_found" | "error";

type HostTenantState = {
  host: string | null;
  status: HostTenantStatus;
  tenant: ResolvedHostTenant | null;
  error: string | null;
};

const initialState: HostTenantState = {
  host: null,
  status: "idle",
  tenant: null,
  error: null,
};

function isFresh(expiresAt: string) {
  return Date.parse(expiresAt) > Date.now();
}

function readStorage(host: string): HostTenantCacheEntry | null {
  try {
    const raw = window.sessionStorage.getItem(`${STORAGE_PREFIX}${host}`);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as HostTenantCacheEntry;
  } catch {
    return null;
  }
}

function writeStorage(host: string, entry: HostTenantCacheEntry) {
  try {
    window.sessionStorage.setItem(`${STORAGE_PREFIX}${host}`, JSON.stringify(entry));
  } catch {
    // Ignore storage failures — resolution just runs again next time.
  }
}

function isNotFoundError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } } | undefined)?.response
    ?.status;
  return status === 404;
}

export const resolveHostTenantAsync = createAsyncThunk<
  { host: string; tenant: ResolvedHostTenant | null },
  { host: string },
  { rejectValue: string }
>("hostTenant/resolve", async ({ host }, { rejectWithValue }) => {
  const cached = readStorage(host);
  if (cached && isFresh(cached.expiresAt)) {
    return { host, tenant: cached.tenant };
  }

  try {
    const tenant = await resolveTenantByHost(host);
    writeStorage(host, {
      tenant,
      expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
    });
    return { host, tenant };
  } catch (error) {
    // An unmapped host (local dev, a marketing domain, etc.) is expected —
    // fall back to default branding instead of failing app boot.
    if (isNotFoundError(error)) {
      writeStorage(host, {
        tenant: null,
        expiresAt: new Date(Date.now() + CACHE_TTL_MS).toISOString(),
      });
      return { host, tenant: null };
    }

    return rejectWithValue(
      error instanceof Error ? error.message : "Failed to resolve tenant for host.",
    );
  }
});

const hostTenantSlice = createSlice({
  name: "hostTenant",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resolveHostTenantAsync.pending, (state, action) => {
        state.status = "loading";
        state.host = action.meta.arg.host;
        state.error = null;
      })
      .addCase(resolveHostTenantAsync.fulfilled, (state, action) => {
        state.host = action.payload.host;
        state.tenant = action.payload.tenant;
        state.status = action.payload.tenant ? "resolved" : "not_found";
        state.error = null;
      })
      .addCase(resolveHostTenantAsync.rejected, (state, action) => {
        state.status = "error";
        state.tenant = null;
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : (action.error.message ?? "Failed to resolve tenant for host.");
      });
  },
});

export default hostTenantSlice.reducer;
